/* eslint-disable @typescript-eslint/no-use-before-define */
import { Ellipsis, Icon, Popover, Tooltip } from '@zoom/components';
import classnames from 'classnames';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UncontrolledTooltip } from 'reactstrap';

import { Skill } from '../../../../graphql/components';
import { formatNameOfPerson } from '../../../../utils/helpers';
import { Agent } from '../../../Agents/types';
import { getYLabelTag } from '../../constants';
import { AgentDaySettings, QueuesWithColors } from '../../types';
import { CloneScheduleMenu } from '../CloneScheduleMenu';
import { unselectBrowserSelection } from '../layoutUtils';
import { AgentContextMenu } from './AgentContextMenu';
import { AgentWorkplace } from './AgentWorkplace';
import { ScheduledHoursPerAgent } from './ScheduledHoursPerAgent';

export const emptyScheduledHours = {
  workDuration: 0,
  totalDuration: 0,
};

export type ScheduleHours = typeof emptyScheduledHours;

export interface YLabelProps extends WithTranslation {
  agent: Agent;
  agentDaySettings?: AgentDaySettings;
  clearSchedule?: () => void;
  cloneSchedule?: (selectedDates: Date[]) => void;
  copySchedule?: () => void;
  daySettingsActive: boolean;
  index: number;
  isAutoScheduleLimitExceeded: boolean;
  isContextMenuEnabled: boolean;
  isMultiQueueSchedule: boolean;
  maxDate: Date;
  minDate: Date;
  pasteSchedule?: () => void;
  pickedTime: Date;
  pinAllScheduleElements: () => void;
  pinElementsInCurrentDay: () => void;
  pinnableElementsCount: number;
  pinnableElementsInCurrentDayCount: number;
  pinnableSelectedElementsInCurrentDayCount: number;
  pinnedElementsCount: number;
  pinnedElementsInCurrentDayCount: number;
  pinnedSelectedElementsInCurrentDayCount: number;
  pinSelectedElements: () => void;
  queues: QueuesWithColors;
  removeAgent?: () => void;
  scheduledHours?: ScheduleHours;
  scrollbarContainerRef: HTMLElement | null;
  selectAll?: () => void;
  skills: Record<string, Skill>;
  triggerCreateAutoScheduleJob?: (selectedAgentIds?: string[]) => Promise<void>;
  unpinAllScheduleElements: () => void;
  unpinElementsInCurrentDay: () => void;
  unpinSelectedElements: () => void;
  workplaceChange: () => void;
}

export const YLabelComponent: React.FC<YLabelProps> = ({
  agent,
  agent: { id: agentId, name, surname, active, deleted, isNotAssigned, shiftTemplateIds },
  agentDaySettings,
  clearSchedule,
  cloneSchedule,
  copySchedule,
  daySettingsActive,
  index,
  isAutoScheduleLimitExceeded,
  isContextMenuEnabled,
  isMultiQueueSchedule,
  maxDate,
  minDate,
  pasteSchedule,
  pickedTime,
  pinAllScheduleElements,
  pinElementsInCurrentDay,
  pinnableElementsCount,
  pinnableElementsInCurrentDayCount,
  pinnableSelectedElementsInCurrentDayCount,
  pinnedElementsCount,
  pinnedElementsInCurrentDayCount,
  pinnedSelectedElementsInCurrentDayCount,
  pinSelectedElements,
  queues,
  removeAgent,
  scheduledHours: { workDuration, totalDuration } = emptyScheduledHours,
  scrollbarContainerRef,
  selectAll,
  skills,
  t,
  triggerCreateAutoScheduleJob,
  unpinAllScheduleElements,
  unpinElementsInCurrentDay,
  unpinSelectedElements,
  workplaceChange,
}) => {
  const yLabelContainerRef = useRef<HTMLDivElement>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const cloneMenuRef = useRef<HTMLDivElement>(null);

  const [isContainerAttached, setContainerAttached] = useState(false);
  const [contextMenuOpened, setContextMenuOpened] = useState(false);
  const [cloneMenuOpened, setCloneMenuOpened] = useState(false);

  const isOpen = contextMenuOpened || cloneMenuOpened;

  const isAgentWorkplaceEnabled = daySettingsActive && isContextMenuEnabled;

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const hideContextMenu = () => {
    setContextMenuOpened(false);
    document.removeEventListener('mousedown', handleContextMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.removeEventListener('scroll', handleContextMenuDismiss);
    }
  };

  const hideCloneMenu = () => {
    setCloneMenuOpened(false);
    document.removeEventListener('mousedown', handleCloneMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.removeEventListener('scroll', handleCloneMenuDismiss);
    }
  };

  const handleContextMenuDismiss = (e: Event) => {
    if (contextMenuRef && contextMenuRef.current && e.target && !contextMenuRef.current.contains(e.target as Node)) {
      hideContextMenu();
    }
    // unselect current selection (agent's name)
    unselectBrowserSelection();
  };

  const handleCloneMenuDismiss = (e: Event) => {
    if (cloneMenuRef && cloneMenuRef.current && e.target && !cloneMenuRef.current.contains(e.target as Node)) {
      hideCloneMenu();
    }
    // unselect current selection (agent's name)
    unselectBrowserSelection();
  };

  const contextMenuHandler = (e: SyntheticEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setContextMenuOpened(true);
    document.addEventListener('mousedown', handleContextMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.addEventListener('scroll', handleContextMenuDismiss);
    }
    return false;
  };

  const handleCopySchedule = () => {
    copySchedule!();
    toast.success(t('schedule.copied'));
    hideContextMenu();
  };

  const handlePasteSchedule = () => {
    pasteSchedule!();
    toast.success(t('schedule.pasted'));
    hideContextMenu();
  };

  const handleSelectAll = () => {
    selectAll!();
    hideContextMenu();
  };

  const handleClearSchedule = () => {
    clearSchedule!();
    toast.success(t('schedule.cleared'));
    hideContextMenu();
  };

  const handleRemoveAgent = () => {
    removeAgent!();
    toast.info(t('person.removed'));
    hideContextMenu();
  };

  const handlePinAllScheduleElements = () => {
    pinAllScheduleElements();
    hideContextMenu();
  };
  const handlePinElementsInCurrentDay = () => {
    pinElementsInCurrentDay();
    hideContextMenu();
  };
  const handlePinSelectedElements = () => {
    pinSelectedElements();
    hideContextMenu();
  };

  const handleUnpinAllScheduleElements = () => {
    unpinAllScheduleElements();
    hideContextMenu();
  };
  const handleUnpinElementsInCurrentDay = () => {
    unpinElementsInCurrentDay();
    hideContextMenu();
  };
  const handleUnpinSelectedElements = () => {
    unpinSelectedElements();
    hideContextMenu();
  };

  const cloneMenuHandler = () => {
    hideContextMenu();
    setCloneMenuOpened(true);

    document.addEventListener('mousedown', handleCloneMenuDismiss);
    if (scrollbarContainerRef) {
      scrollbarContainerRef.addEventListener('scroll', handleCloneMenuDismiss);
    }
    return false;
  };

  const handleTriggerCreateAutoScheduleJob = () => {
    triggerCreateAutoScheduleJob!([agent.id]);
    hideContextMenu();
  };

  /**
   * Queues record object includes also data for queues not participating on schedule,
   * because they are required for production elements from other schedules,
   * but these are not fully defined queues object (eq. not containing name of queue)
   */
  const relevantQueueIds = agent.queueIds?.filter((queueId) => queues[queueId]?.name) ?? [];

  return (
    <>
      <div
        {...(isContextMenuEnabled ? { onContextMenu: contextMenuHandler } : {})}
        data-test-id={getYLabelTag(index)}
        className={classnames('gantt-y-label__wrapper', {
          'selection--disabled gantt-y-label__wrapper--open': isOpen,
          'gantt-y-label__wrapper--not-active': !active || isNotAssigned,
        })}
        ref={yLabelContainerRef}
      >
        <div className={classnames('gantt-y-label__wrapper-inner', { 'pb-1': isMultiQueueSchedule && !isNotAssigned })}>
          <span className="gantt-y-label__details">
            <ScheduledHoursPerAgent
              agent={agent}
              queues={queues}
              skills={skills}
              durationData={{ workDuration, totalDuration, index }}
              scrollbarContainerRef={scrollbarContainerRef}
              date={pickedTime}
            />
            {(!active || isNotAssigned) && (
              <Tooltip
                message={t(
                  `person.${deleted ? 'deleted' : isNotAssigned ? 'notAssignedToAnyOfQueues' : 'notActive'}`,
                  isNotAssigned ? { count: isMultiQueueSchedule ? 2 : 1 } : {}
                )}
                placement="right"
              >
                <Icon
                  data-test-id={`yLabel:agentId(${agentId}):inactive`}
                  kind="warning"
                  color="blue-light"
                  size={14}
                  className="gantt-y-label__details__icon-inactive-agent"
                />
              </Tooltip>
            )}
          </span>
          {/* @ts-expect-error: Incompatible type of FC from React < v18 */}
          <Ellipsis className={classnames('gantt-y-label__name')}>{formatNameOfPerson(name, surname)}</Ellipsis>
          {active && !isNotAssigned && agentDaySettings && (
            <span className="gantt-y-label__workplace">
              <AgentWorkplace
                isContextMenuEnabled={isAgentWorkplaceEnabled}
                remote={agentDaySettings.remote}
                onWorkplaceChange={workplaceChange}
                scrollbarContainerRef={scrollbarContainerRef}
              />
            </span>
          )}
        </div>
        {isMultiQueueSchedule && !isNotAssigned && (
          <ul
            className={classnames('gantt-y-label__indicators-wrapper', {
              'gantt-y-label__indicators-wrapper--shrinked-offset': relevantQueueIds.length === 10,
            })}
            data-test-id={`yLabel:agentId(${agentId}):queues-indicator-container`}
          >
            {relevantQueueIds.map((queueId) => {
              const { color, name: queueName } = queues[queueId];
              const indicatorId = `indicator-${agentId}-${queueId}`;

              return (
                <li
                  className="gantt-y-label__queue-indicator"
                  data-test-id={`yLabel:agentId(${agentId}):queue-indicator(${queueId})`}
                  id={indicatorId}
                  key={indicatorId}
                  style={{
                    backgroundColor: color,
                  }}
                >
                  <span className="sr-only">{queueName}</span>
                  <UncontrolledTooltip delay={0} fade={false} target={indicatorId}>
                    {queueName}
                  </UncontrolledTooltip>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {isContextMenuEnabled && isContainerAttached && (
        <Popover
          trigger="contextmenu"
          isOpen={contextMenuOpened}
          target={yLabelContainerRef.current!}
          placement="bottom"
          fade={false}
          boundariesElement="window"
          className="person-context-menu"
        >
          <AgentContextMenu
            clearAgentSchedule={clearSchedule ? handleClearSchedule : undefined}
            cloneAgentSchedule={cloneSchedule ? cloneMenuHandler : undefined}
            copyAgentSchedule={copySchedule ? handleCopySchedule : undefined}
            isAutoScheduleLimitExceeded={isAutoScheduleLimitExceeded}
            pasteAgentSchedule={pasteSchedule && active && !isNotAssigned ? handlePasteSchedule : undefined}
            pinAllScheduleElements={handlePinAllScheduleElements}
            pinElementsInCurrentDay={handlePinElementsInCurrentDay}
            pinnableElementsCount={pinnableElementsCount}
            pinnableElementsInCurrentDayCount={pinnableElementsInCurrentDayCount}
            pinnableSelectedElementsInCurrentDayCount={pinnableSelectedElementsInCurrentDayCount}
            pinnedElementsCount={pinnedElementsCount}
            pinnedElementsInCurrentDayCount={pinnedElementsInCurrentDayCount}
            pinnedSelectedElementsInCurrentDayCount={pinnedSelectedElementsInCurrentDayCount}
            pinSelectedElements={handlePinSelectedElements}
            removeAgentFromSchedule={removeAgent ? handleRemoveAgent : undefined}
            selectAllAgentSchedule={selectAll ? handleSelectAll : undefined}
            triggerCreateAutoScheduleJob={triggerCreateAutoScheduleJob ? handleTriggerCreateAutoScheduleJob : undefined}
            unpinAllScheduleElements={handleUnpinAllScheduleElements}
            unpinElementsInCurrentDay={handleUnpinElementsInCurrentDay}
            unpinSelectedElements={handleUnpinSelectedElements}
            wrapperRef={contextMenuRef}
            shiftTemplateIds={shiftTemplateIds}
          />
        </Popover>
      )}

      {cloneMenuOpened && isContainerAttached && (
        <Popover
          isOpen={cloneMenuOpened}
          target={yLabelContainerRef.current!}
          placement="top"
          fade={false}
          boundariesElement="window"
          className="clone-menu"
        >
          <CloneScheduleMenu
            cloneSchedule={cloneSchedule}
            handleCancel={hideCloneMenu}
            maxDate={maxDate}
            minDate={minDate}
            pickedTime={pickedTime}
            snackbarMessage="schedule.cloned"
            wrapperRef={cloneMenuRef}
          />
        </Popover>
      )}
    </>
  );
};

export const YLabel = withTranslation()(YLabelComponent);
